export type DicomPatientSex = 'M' | 'F' | 'O' | 'U';
export type DicomClassification =
  | 'T1w'
  | 'T2w'
  | 'FLAIR'
  | 'DWI'
  | 'SWI'
  | 'MRA'
  | 'CT'
  | 'Unknown';
export type DicomClassificationConfidence = 'high' | 'medium' | 'low';
export type DicomGeometryStatus = 'valid' | 'warning' | 'invalid';
export type DicomPixelDataStatus = 'valid' | 'warning' | 'invalid' | 'not_checked';

export interface DicomPatientMetadata {
  patient_name: string | null;
  patient_id: string | null;
  birth_date: string | null;
  sex: DicomPatientSex | null;
  study_instance_uid: string | null;
  study_date: string | null;
}

export interface DicomStudyAnalysis {
  study_instance_uid: string;
  study_date: string | null;
  study_description: string | null;
  accession_number: string | null;
  series_count: number;
  instance_count: number;
}

export interface DicomSeriesAnalysis {
  series_instance_uid: string;
  study_instance_uid: string;
  study_date: string | null;
  study_description: string | null;
  series_number: number | null;
  series_description: string | null;
  protocol_name: string | null;
  sequence_name: string | null;
  modality: string;
  classification: DicomClassification;
  classification_confidence: DicomClassificationConfidence;
  classification_reasons: string[];
  instance_count: number;
  total_frame_count?: number;
  rows: number | null;
  columns: number | null;
  samples_per_pixel?: number | null;
  bits_allocated?: number | null;
  bits_stored?: number | null;
  high_bit?: number | null;
  pixel_representation?: number | null;
  photometric_interpretation?: string | null;
  pixel_spacing?: number[] | null;
  slice_thickness?: number | null;
  spacing_between_slices?: number | null;
  image_orientation_patient?: number[] | null;
  image_type?: string[];
  number_of_frames?: number | null;
  manufacturer?: string | null;
  manufacturer_model_name?: string | null;
  magnetic_field_strength?: number | null;
  frame_of_reference_uid?: string | null;
  repetition_time: number | null;
  echo_time: number | null;
  inversion_time: number | null;
  flip_angle: number | null;
  mr_acquisition_type: string | null;
  is_localizer: boolean;
  is_derived: boolean;
  is_secondary_capture: boolean;
  eligible_for_fixed: boolean;
  geometry_status?: DicomGeometryStatus;
  geometry_issues?: string[];
  pixel_data_status?: DicomPixelDataStatus;
  pixel_data_issues?: string[];
  is_volume_candidate?: boolean;
  thumbnail_data_urls?: string[];
}

export interface PreparedNiftiAsset {
  asset_id: string;
  classification: DicomClassification;
  series_instance_uid: string;
  file_name: string;
  size_bytes: number;
  sha256: string;
  download_url: string;
  cache_key: string;
  variant?: 'original' | 'aligned';
  alignment_mode?: 'auto' | 'manual' | null;
  source_asset_id?: string | null;
  reference_asset_id?: string | null;
}

export interface DicomAnalyzeSummary {
  received_file_count: number;
  dicom_file_count: number;
  ignored_file_count: number;
  duplicate_instance_count: number;
  study_count: number;
  series_count: number;
}

export interface DicomAnalysisWarning {
  code: string;
  message: string;
  count?: number | null;
}

export type DicomAlignmentStatus = 'pending' | 'running' | 'ready' | 'failed';

export interface DicomAnalyzeResponse {
  source_type?: 'dicom' | 'nifti';
  upload_id: string;
  patient: DicomPatientMetadata;
  studies: DicomStudyAnalysis[];
  series: DicomSeriesAnalysis[];
  summary: DicomAnalyzeSummary;
  warnings: DicomAnalysisWarning[];
  workspace_path?: string | null;
  prepared_nifti_assets?: PreparedNiftiAsset[];
  aligned_nifti_assets?: PreparedNiftiAsset[];
  alignment_status?: DicomAlignmentStatus;
}

export interface DicomSeriesRenderInstance {
  sop_instance_uid: string;
  instance_number: number | null;
  image_position_patient: number[] | null;
  image_orientation_patient: number[] | null;
  slice_location: number | null;
  rows: number | null;
  columns: number | null;
  number_of_frames: number | null;
  transfer_syntax_uid: string | null;
  instance_url: string;
}

export interface DicomSeriesRenderResponse {
  upload_id: string;
  study_instance_uid: string;
  series_instance_uid: string;
  modality: string | null;
  series_number: number | null;
  series_description: string | null;
  instance_count: number;
  rows: number | null;
  columns: number | null;
  number_of_frames: number | null;
  instances: DicomSeriesRenderInstance[];
  warnings: DicomAnalysisWarning[];
}

function isDicomPatientMetadata(value: unknown): value is DicomPatientMetadata {
  return Boolean(
    value &&
      typeof value === 'object' &&
      'study_instance_uid' in value &&
      (value.study_instance_uid === null || typeof value.study_instance_uid === 'string')
  );
}

function isDicomStudyAnalysis(value: unknown): value is DicomStudyAnalysis {
  return Boolean(
    value &&
      typeof value === 'object' &&
      'study_instance_uid' in value &&
      typeof value.study_instance_uid === 'string' &&
      'series_count' in value &&
      typeof value.series_count === 'number' &&
      'instance_count' in value &&
      typeof value.instance_count === 'number'
  );
}

function isDicomSeriesAnalysis(value: unknown): value is DicomSeriesAnalysis {
  return Boolean(
    value &&
      typeof value === 'object' &&
      'series_instance_uid' in value &&
      typeof value.series_instance_uid === 'string' &&
      'study_instance_uid' in value &&
      typeof value.study_instance_uid === 'string' &&
      'study_date' in value &&
      (value.study_date === null || typeof value.study_date === 'string') &&
      'study_description' in value &&
      (value.study_description === null || typeof value.study_description === 'string') &&
      'classification' in value &&
      typeof value.classification === 'string' &&
      'eligible_for_fixed' in value &&
      typeof value.eligible_for_fixed === 'boolean'
  );
}

function isDicomAnalysisWarning(value: unknown): value is DicomAnalysisWarning {
  return Boolean(
    value &&
      typeof value === 'object' &&
      'code' in value &&
      typeof value.code === 'string' &&
      'message' in value &&
      typeof value.message === 'string'
  );
}

function isNullableNumber(value: unknown): value is number | null {
  return value === null || typeof value === 'number';
}

function isNullableString(value: unknown): value is string | null {
  return value === null || typeof value === 'string';
}

function isNullableNumberArray(value: unknown): value is number[] | null {
  return value === null || (Array.isArray(value) && value.every(item => typeof item === 'number'));
}

function isDicomSeriesRenderInstance(value: unknown): value is DicomSeriesRenderInstance {
  return Boolean(
    value &&
      typeof value === 'object' &&
      'sop_instance_uid' in value &&
      typeof value.sop_instance_uid === 'string' &&
      'instance_number' in value &&
      isNullableNumber(value.instance_number) &&
      'image_position_patient' in value &&
      isNullableNumberArray(value.image_position_patient) &&
      'image_orientation_patient' in value &&
      isNullableNumberArray(value.image_orientation_patient) &&
      'slice_location' in value &&
      isNullableNumber(value.slice_location) &&
      'rows' in value &&
      isNullableNumber(value.rows) &&
      'columns' in value &&
      isNullableNumber(value.columns) &&
      'number_of_frames' in value &&
      isNullableNumber(value.number_of_frames) &&
      'transfer_syntax_uid' in value &&
      isNullableString(value.transfer_syntax_uid) &&
      'instance_url' in value &&
      typeof value.instance_url === 'string'
  );
}

export function isDicomAnalyzeResponse(value: unknown): value is DicomAnalyzeResponse {
  return Boolean(
    value &&
      typeof value === 'object' &&
      'upload_id' in value &&
      typeof value.upload_id === 'string' &&
      (!('source_type' in value) || ['dicom', 'nifti'].includes(String(value.source_type))) &&
      'patient' in value &&
      isDicomPatientMetadata(value.patient) &&
      'studies' in value &&
      Array.isArray(value.studies) &&
      value.studies.every(isDicomStudyAnalysis) &&
      'series' in value &&
      Array.isArray(value.series) &&
      value.series.every(isDicomSeriesAnalysis) &&
      'summary' in value &&
      value.summary &&
      typeof value.summary === 'object' &&
      'series_count' in value.summary &&
      typeof value.summary.series_count === 'number' &&
      'study_count' in value.summary &&
      typeof value.summary.study_count === 'number' &&
      'warnings' in value &&
      Array.isArray(value.warnings) &&
      value.warnings.every(isDicomAnalysisWarning) &&
      (!('alignment_status' in value) ||
        ['pending', 'running', 'ready', 'failed'].includes(String(value.alignment_status)))
  );
}

export function isDicomSeriesRenderResponse(value: unknown): value is DicomSeriesRenderResponse {
  return Boolean(
    value &&
      typeof value === 'object' &&
      'upload_id' in value &&
      typeof value.upload_id === 'string' &&
      'study_instance_uid' in value &&
      typeof value.study_instance_uid === 'string' &&
      'series_instance_uid' in value &&
      typeof value.series_instance_uid === 'string' &&
      'modality' in value &&
      isNullableString(value.modality) &&
      'series_number' in value &&
      isNullableNumber(value.series_number) &&
      'series_description' in value &&
      isNullableString(value.series_description) &&
      'instance_count' in value &&
      typeof value.instance_count === 'number' &&
      'rows' in value &&
      isNullableNumber(value.rows) &&
      'columns' in value &&
      isNullableNumber(value.columns) &&
      'number_of_frames' in value &&
      isNullableNumber(value.number_of_frames) &&
      'instances' in value &&
      Array.isArray(value.instances) &&
      value.instances.every(isDicomSeriesRenderInstance) &&
      'warnings' in value &&
      Array.isArray(value.warnings) &&
      value.warnings.every(isDicomAnalysisWarning)
  );
}
